// Pessoas autorizadas do cliente (aba Cliente, Editar/Criar Usuário —
// pedido 2026-10-08). Cada pessoa pode receber por WhatsApp os avisos de
// Falha de Comunicação e/ou Corte de Energia dos veículos do cliente
// (backend: communication-failure/device-whatsapp-recipients.ts).
import { Plus, Trash2, UserCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { AuthorizedContact } from "@/services/gpsdrive-client-profile.service";

export const MAX_AUTHORIZED_CONTACTS = 10;

export function blankAuthorizedContact(): AuthorizedContact {
  return { name: "", phone: "", relationship: null, communicationFailure: true, powerCut: true };
}

// Linhas totalmente vazias são descartadas; linha começada e incompleta
// devolve erro (nunca salva pela metade).
export function normalizeAuthorizedContacts(
  list: AuthorizedContact[],
): { contacts: AuthorizedContact[]; error: string | null } {
  const contacts: AuthorizedContact[] = [];
  for (const c of list) {
    const name = c.name.trim();
    const phone = c.phone.replace(/\D/g, "");
    const relationship = c.relationship?.trim() || null;
    if (!name && !phone && !relationship) continue;
    if (!name) return { contacts, error: "Informe o nome de cada pessoa autorizada" };
    if (phone.length < 10 || phone.length > 15)
      return { contacts, error: `Celular inválido para a pessoa autorizada "${name}" (use DDD + número)` };
    contacts.push({ name, phone, relationship, communicationFailure: c.communicationFailure, powerCut: c.powerCut });
  }
  return { contacts, error: null };
}

export function sameAuthorizedContacts(a: AuthorizedContact[], b: AuthorizedContact[]): boolean {
  return JSON.stringify(normalizeAuthorizedContacts(a).contacts) === JSON.stringify(normalizeAuthorizedContacts(b).contacts);
}

export function AuthorizedContactsEditor({
  value,
  onChange,
  disabled,
}: {
  value: AuthorizedContact[];
  onChange: (next: AuthorizedContact[]) => void;
  disabled?: boolean;
}) {
  const patch = (index: number, changes: Partial<AuthorizedContact>) =>
    onChange(value.map((c, i) => (i === index ? { ...c, ...changes } : c)));

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="text-sm font-semibold">Pessoas autorizadas</div>
            <div className="text-xs text-muted-foreground">
              Também recebem pelo WhatsApp os avisos dos veículos deste cliente.
            </div>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || value.length >= MAX_AUTHORIZED_CONTACTS}
          onClick={() => onChange([...value, blankAuthorizedContact()])}
        >
          <Plus className="mr-1 h-4 w-4" /> Adicionar
        </Button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          Nenhuma pessoa autorizada.
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((c, i) => (
            <div key={i} className="rounded-md border border-border bg-background p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_180px_150px_auto]">
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">Nome</Label>
                  <Input
                    value={c.name}
                    disabled={disabled}
                    onChange={(e) => patch(i, { name: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">Celular (WhatsApp)</Label>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="DDD + número"
                    value={c.phone}
                    disabled={disabled}
                    onChange={(e) => patch(i, { phone: e.target.value.replace(/\D/g, "") })}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs text-muted-foreground">Parentesco/relação</Label>
                  <Input
                    placeholder="Ex.: esposa, sócio"
                    value={c.relationship ?? ""}
                    disabled={disabled}
                    onChange={(e) => patch(i, { relationship: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div className="flex items-end justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-destructive hover:text-destructive"
                    disabled={disabled}
                    onClick={() => onChange(value.filter((_, j) => j !== i))}
                    title="Remover"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
                <span className="text-muted-foreground">Recebe:</span>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <Checkbox
                    checked={c.communicationFailure}
                    disabled={disabled}
                    onCheckedChange={(v) => patch(i, { communicationFailure: v === true })}
                  />
                  Falha de comunicação
                </label>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <Checkbox
                    checked={c.powerCut}
                    disabled={disabled}
                    onCheckedChange={(v) => patch(i, { powerCut: v === true })}
                  />
                  Corte de energia / bateria violada
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
